//Proofed
import QueryStatsIcon from '@mui/icons-material/QueryStats'
import InsightsIcon from '@mui/icons-material/Insights'
import CheckIcon from '@mui/icons-material/Check'
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents'
import SearchIcon from '@mui/icons-material/Search'
import SchoolIcon from '@mui/icons-material/School'
import type { HowItWorksStep } from '../components/HowItWorksSection'

// Hero right-panel highlights
export const marketAnalysisHighlights = [
    'Understand the size and shape of the market relevant to your products',
    'Identify which buyers are most likely to purchase what you do',
    'Benchmark your competition and understand their market position',
    'Know what it takes to win before you write your first bid',
    'Make a go/no-go decision based on data, not assumption',
]

// Challenge cards  section 2
export const marketAnalysisChallenges = [
    {
        heading: "You don't know if the public sector market is worth pursuing for your business",
        body: "The UK public sector spends over £300bn annually, but not every business has an addressable market within it. Before investing in BD capability, you need to know whether the opportunity is real and how large it actually is for your specific products and services",
    },
    {
        heading: "You don't know who the right buyers are or how they go to market",
        body: "Government procurement is fragmented across hundreds of buyers operating different frameworks and procurement routes. Without intelligence on which buyers are active, what they've been spending and how they procure, you're targeting blind",
    },
    {
        heading: "You're investing in a decision without the data to support it",
        body: "Most businesses that enter public sector either over-invest before understanding the market or under-invest and fail to get traction. A go/no-go decision made without evidence carries real cost in budget, time and leadership attention",
    },
]

// Recommended solution stack  section 3
export const marketAnalysisSolutionStack = [
    {
        Icon: QueryStatsIcon,
        title: 'Market Research Study',
        body: 'A bespoke intelligence report covering your sector, key buyers, procurement patterns, competitor landscape and whether the opportunity justifies investment. Produced by our advisory team, specific to your business',
        tag: 'Advisory',
        link: '/products/bid-services',
        linkLabel: 'Learn about Bid Services',
    },
    {
        Icon: SearchIcon,
        title: 'CFP Core Pro',
        body: 'Once you\'ve decided to proceed, Core Pro gives you live opportunity monitoring and buyer intelligence from day one. Tender alerts, buyer research, competitor tracking: the complete market picture',
        tag: 'Platform',
        link: '/products/core',
        linkLabel: 'Learn about Core Pro',
    },
    {
        Icon: SchoolIcon,
        title: 'Bid & BD Service',
        body: 'If you decide to enter, our advisory team can support your first submissions and help you build a BD process from scratch, with strategy, bid writing and delivery handled by people who know the market',
        tag: 'Advisory',
        link: '/products/bid-services',
        linkLabel: 'Learn about Bid Services',
    },
]

// Journey steps  section 4
export const marketAnalysisSteps: HowItWorksStep[] = [
    {
        num: '1',
        Icon: QueryStatsIcon,
        title: 'Commission Your Market Study',
        body: 'Brief our advisory team on your business, your products and your target markets. We take it from there, designing a research programme specific to your sector and opportunity',
    },
    {
        num: '2',
        Icon: InsightsIcon,
        title: 'Review the Intelligence',
        body: 'Receive your bespoke report covering buyer landscape, competitor positions, procurement patterns, contract values and a go/no-go recommendation backed by data',
    },
    {
        num: '3',
        Icon: CheckIcon,
        title: 'Make Your Decision',
        body: 'With clear evidence in hand, decide with confidence whether to proceed, which markets to prioritise and what investment is justified. No more guesswork',
    },
    {
        num: '4',
        Icon: EmojiEventsIcon,
        title: 'Enter with Confidence',
        body: 'If you proceed, you start from the right entry point with the right tools: Core Pro for monitoring, advisory support for your first bids and a strategy built on what actually wins',
    },
]

// Outcomes checklist  section 5
export const marketAnalysisOutcomes = [
    'Know whether the public sector market is large enough and accessible enough for your business',
    'Understand who your buyers would be and how they procure',
    'See what competitors are winning and whether you can realistically compete',
    'Have a clear go/no-go recommendation backed by data',
    'If you proceed, start from the right entry point with the right tools',
]

// Self-qualification checklist  section 6
export const marketAnalysisQualifiers = [
    'You run an established business and are seriously considering the public sector market',
    'You want to make this decision based on evidence, not assumption',
    'You have the capacity to deliver public sector work if the market is right',
    'You\'re willing to invest in understanding the market before investing in BD',
    'You want expert guidance, not just a list of tenders',
]


// FAQs  section 8
export const marketAnalysisFaqs = [
    {
        q: 'What is a Market Research Study?',
        a: "A Market Research Study is a bespoke intelligence report produced by our advisory team. It covers the buyers most likely to purchase your products or services, their procurement patterns and typical contract values, the competitive landscape, relevant frameworks and procurement routes and a clear recommendation on whether and how to enter the market. It is specific to your business, not a generic sector overview.",
    },
    {
        q: 'How long does a Market Research Study take?',
        a: "A standard Market Research Study typically takes 3-4 weeks from the initial briefing to delivery of the final report. Complex sectors with multiple buyer types or highly fragmented procurement may take slightly longer. We'll agree a timeline with you at the outset and keep you updated throughout.",
    },
    {
        q: 'What if the study shows the market isn\'t right for us?',
        a: "Then we'll tell you that, clearly and honestly. The value of a Market Research Study is an evidence-based decision, whatever that decision turns out to be. Many clients find that the study redirects them to a better-fit market segment rather than ruling out public sector entirely. Either outcome saves significantly more than the cost of the study.",
    },
    {
        q: 'How is this different from us just using CFP Core Pro to explore?',
        a: "CFP Core Pro gives you live opportunity monitoring and buyer intelligence. It shows you what's being procured right now. A Market Research Study goes deeper: it analyses historical award data, competitor positions, procurement patterns over time and the strategic landscape. It answers 'should we enter?' rather than 'what's live today?' The two are complementary, not substitutes.",
    },
    {
        q: 'What happens after the study?',
        a: "We present the findings in a structured debrief session and answer your questions. If you decide to proceed, we recommend the right starting point, typically CFP Core Pro for opportunity monitoring, potentially alongside advisory support for your first submissions. We'll produce a clear next-steps plan as part of the study deliverable.",
    },
    {
        q: 'Do we need to commit to the full solution upfront?',
        a: "No. The Market Research Study is a standalone engagement. You can commission it without any commitment to take further products or services. If you decide to proceed after the study, you then choose the right combination of platform and advisory support and we'll help you design that. Many clients start with just the study and build from there.",
    },
]

// Related solution links  section 9
export const marketAnalysisRelated = [
    {
        headline: 'New to Government Contracts',
        body: 'Decided the market is right for you? Our structured route-in for new entrants covers platform setup, market intelligence and expert bid preparation for your first submission',
        link: '/solutions/new-to-government',
        tag: 'Getting Started',
    },
    {
        headline: 'Grow Faster in UK Public Sector',
        body: 'Already selling to government and want to do it better? Stronger monitoring, AI bid writing and a Win Review to sharpen your BD process and improve win rates',
        link: '/solutions/grow-faster-uk-public-sector',
        tag: 'Getting Started',
    },
]
